'use client'

import { useState } from 'react'
import { fulfillmentApi, type FulfillmentApi } from '@entities/fulfillment'
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxPopup,
  ComboboxPositioner,
} from '@ui/base/combobox'
import { Field, FieldError, FieldLabel } from '@ui/base/field'

import { useDirectorySearch } from '../lib/use-directory-search'

const searchCategories = (query: string) =>
  fulfillmentApi.categoryOptions({ q: query, limit: 20 }).then((response) => response.data.data?.categories ?? [])

/** Категория товара — предмет Wildberries; продавец может и просто вписать своё название. */
export const CategoryPicker = ({
  subjectName,
  onChange,
}: {
  subjectName: string
  onChange: (category: { subjectId: number | null; subjectName: string }) => void
}) => {
  const [isListOpen, setIsListOpen] = useState(false)
  const { options, isFailed } = useDirectorySearch(subjectName, searchCategories)

  return (
    <Field data-invalid={isFailed}>
      <FieldLabel htmlFor="request-category">Категория товара</FieldLabel>
      <Combobox
        items={options}
        filter={null}
        value={null}
        onValueChange={(option: FulfillmentApi.Category | null) => {
          if (option) {
            onChange({ subjectId: option.subject_id, subjectName: option.subject_name })
            setIsListOpen(false)
          }
        }}
        inputValue={subjectName}
        onInputValueChange={(value, details) => {
          if (details.reason !== 'input-change') {
            return
          }
          // Своё название тоже сохраняем: предмет из справочника подставится, если продавец его выберет.
          onChange({ subjectId: null, subjectName: value })
          setIsListOpen(Boolean(value.trim()))
        }}
        itemToStringLabel={(option: FulfillmentApi.Category) => option.subject_name}
        open={isListOpen && options.length > 0}
        onOpenChange={setIsListOpen}
        openOnInputClick={false}
      >
        <ComboboxInput id="request-category" placeholder="Например, Футболки" />
        <ComboboxPositioner>
          <ComboboxPopup>
            <ComboboxEmpty>Категория не найдена</ComboboxEmpty>
            <ComboboxList>
              {options.map((option) => (
                <ComboboxItem key={option.subject_id} value={option}>
                  <ComboboxItemIndicator />
                  {option.subject_name}
                </ComboboxItem>
              ))}
            </ComboboxList>
          </ComboboxPopup>
        </ComboboxPositioner>
      </Combobox>
      <FieldError>
        {isFailed ? 'Справочник категорий недоступен. Название можно вписать своими словами.' : ''}
      </FieldError>
    </Field>
  )
}
